import React, { useState } from 'react';
import { ArrowLeft, HardHat, Camera, PaperPlaneTilt, Copy, ArrowsLeftRight, CalendarBlank, Heart, UserPlus, CreditCard, DeviceMobile, ArrowRight, CheckCircle, NumberOne, NumberTwo, NumberThree, NumberFour, NumberFive, Question } from '@phosphor-icons/react';

const STEPS = [
  {
    id: 'create',
    title: "Créez votre compte",
    icon: UserPlus,
    color: "#8B5CF6",
    description: "Inscrivez-vous gratuitement en 30 secondes. 2 posts d'essai offerts.",
    details: [
      "Cliquez sur \"Essayer gratuitement\"",
      "Remplissez votre nom, email et mot de passe",
      "Si vous avez un code promo, entrez-le pour un accès Pro gratuit",
      "C'est prêt — vous êtes connecté !"
    ]
  },
  {
    id: 'generate',
    title: "Générez votre premier post",
    icon: PaperPlaneTilt,
    color: "#8B5CF6",
    description: "L'IA crée le post parfait en quelques secondes.",
    details: [
      "Choisissez la plateforme : Facebook ou WhatsApp",
      "Sélectionnez votre ville (Mamoudzou, Sada, Bandrélé...)",
      "Choisissez le type de travaux (Toiture, Clim, Portail...)",
      "Décrivez votre chantier ou utilisez un template rapide",
      "Ajoutez une photo si vous voulez (optionnel)",
      "Cliquez sur \"Générer le post\" — l'IA fait le reste !"
    ]
  },
  {
    id: 'copy',
    title: "Copiez et publiez",
    icon: Copy,
    color: "#22C55E",
    description: "Un clic pour copier, collez sur Facebook ou WhatsApp.",
    details: [
      "Le post apparaît dans l'aperçu avec les emojis",
      "Cliquez sur \"Copier le post\"",
      "Ouvrez Facebook ou WhatsApp",
      "Collez le texte — c'est publié !",
      "Vous pouvez aussi modifier le post avant de le copier"
    ]
  },
  {
    id: 'avant-apres',
    title: "Mode Avant / Après",
    icon: ArrowsLeftRight,
    color: "#F59E0B",
    description: "Montrez la transformation — le contenu le plus viral du BTP.",
    details: [
      "Passez en mode \"Avant / Après\" dans le générateur",
      "Uploadez la photo AVANT (le chantier brut)",
      "Uploadez la photo APRÈS (le travail terminé)",
      "L'IA crée un post comparatif percutant",
      "Les posts Avant/Après génèrent 3x plus d'engagement !"
    ]
  },
  {
    id: 'gallery',
    title: "Partagez en galerie",
    icon: Heart,
    color: "#EF4444",
    description: "Publiez vos meilleurs projets et gagnez en visibilité.",
    details: [
      "Après avoir généré un post, cliquez \"Publier en galerie\"",
      "Votre projet devient visible par toute la communauté",
      "Les visiteurs peuvent aimer vos posts",
      "Les artisans avec le plus de likes sont dans le \"Top Artisan\" !",
      "Plus de likes = plus de visibilité = plus de clients"
    ]
  },
  {
    id: 'calendar',
    title: "Planifiez vos publications",
    icon: CalendarBlank,
    color: "#3B82F6",
    description: "Organisez vos posts pour la semaine avec le calendrier.",
    details: [
      "Allez dans l'onglet \"Calendrier\"",
      "Consultez les meilleurs moments pour poster à Mayotte",
      "Cliquez sur un jour pour planifier un post",
      "Remplissez le titre, la ville et le type de travaux",
      "L'app vous rappellera de publier le jour venu !"
    ]
  },
  {
    id: 'install',
    title: "Installez l'app",
    icon: DeviceMobile,
    color: "#8B5CF6",
    description: "Ajoutez Shanga Pro sur votre téléphone comme une vraie app.",
    details: [
      "Ouvrez le lien de l'app dans Chrome (Android) ou Safari (iPhone)",
      "Android : menu ⋮ → \"Ajouter à l'écran d'accueil\"",
      "iPhone : bouton partage ↑ → \"Sur l'écran d'accueil\"",
      "L'icône Shanga Pro apparaît sur votre téléphone",
      "Lancez-la directement depuis le chantier !"
    ]
  }
];

const FAQ = [
  { q: "C'est gratuit ?", a: "Vous avez 2 posts d'essai gratuits. Ensuite, l'abonnement Pro est à 17,90€/mois pour des posts illimités." },
  { q: "Je dois être bon en informatique ?", a: "Pas du tout ! Vous choisissez, vous cliquez, l'IA fait le reste. C'est aussi simple qu'envoyer un message WhatsApp." },
  { q: "Les posts sont en français ?", a: "Oui, tous les posts sont rédigés en français avec un ton chaleureux adapté à Mayotte (esprit Karibu)." },
  { q: "Je peux modifier les posts ?", a: "Absolument ! Vous pouvez modifier le texte généré avant de le copier. L'historique garde tous vos posts." },
  { q: "Comment parrainer un ami ?", a: "Allez dans l'onglet \"Parrainage\", copiez votre lien et envoyez-le. Pour chaque ami inscrit, vous gagnez 1 mois Pro gratuit !" },
  { q: "Ça marche sans internet ?", a: "Non, l'app a besoin d'internet pour générer les posts via l'IA. Mais vous pouvez copier et coller les posts même avec une connexion lente." },
];

export default function HowItWorksPage({ onBack, onGetStarted }) {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="how-it-works-page">
      {/* Nav */}
      <nav className="bg-[#09090B] border-b border-[#27272A] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="text-[#A1A1AA] hover:text-[#F4F4F5] transition-colors" data-testid="how-back-btn">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#8B5CF6] flex items-center justify-center">
                <HardHat size={18} weight="duotone" className="text-white" />
              </div>
              <span className="font-bold text-lg text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Guide</span>
            </div>
          </div>
          <button onClick={onGetStarted} data-testid="how-cta-btn"
            className="h-9 px-4 text-sm bg-[#8B5CF6] text-white rounded-lg hover:bg-[#7C3AED] font-medium transition-colors">
            Commencer
          </button>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {/* Hero */}
        <div className="text-center mb-12 animate-fade-in-up">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-3 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Comment utiliser Shanga Pro ?
          </h1>
          <p className="text-lg text-[#A1A1AA]">En 7 étapes simples, devenez un expert du marketing BTP</p>
        </div>

        {/* Steps */}
        <div className="space-y-4 mb-16">
          {STEPS.map((step, i) => (
            <div key={step.id} className="bg-[#18181B] border border-[#27272A] rounded-lg overflow-hidden animate-fade-in-up hover:border-[#8B5CF6]/30 transition-all"
              style={{ animationDelay: `${i * 0.05}s` }} data-testid={`step-${step.id}`}>
              <div className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${step.color}20` }}>
                    <step.icon size={22} weight="duotone" style={{ color: step.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-[#8B5CF6] bg-[#8B5CF6]/10 px-2 py-0.5 rounded">Étape {i + 1}</span>
                    </div>
                    <h3 className="font-bold text-lg text-[#F4F4F5] mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{step.title}</h3>
                    <p className="text-sm text-[#A1A1AA] mb-3">{step.description}</p>
                    <ul className="space-y-1.5">
                      {step.details.map((d, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-[#E4E4E7]">
                          <CheckCircle size={16} weight="duotone" className="text-[#8B5CF6] shrink-0 mt-0.5" />
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <Question size={28} weight="duotone" className="mx-auto text-[#A78BFA] mb-2" />
            <h2 className="text-2xl font-bold tracking-tight text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Questions fréquentes</h2>
          </div>
          <div className="space-y-2">
            {FAQ.map((faq, i) => (
              <div key={i} className="bg-[#18181B] border border-[#27272A] rounded-lg overflow-hidden" data-testid={`faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-5 py-4 text-left flex items-center justify-between hover:bg-[#1F1F23] transition-colors">
                  <span className="font-medium text-sm text-[#F4F4F5]">{faq.q}</span>
                  <ArrowRight size={14} className={`text-[#71717A] transition-transform ${openFaq === i ? 'rotate-90' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 animate-fade-in">
                    <p className="text-sm text-[#A1A1AA] leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center animate-fade-in-up">
          <button onClick={onGetStarted} data-testid="how-final-cta"
            className="h-14 px-8 bg-[#8B5CF6] text-white font-bold text-base rounded-lg hover:bg-[#7C3AED] transition-colors inline-flex items-center gap-2 animate-glow">
            Commencer gratuitement <ArrowRight size={20} weight="bold" />
          </button>
          <p className="text-xs text-[#71717A] mt-3">2 posts d'essai gratuits. Sans carte bancaire.</p>
        </div>
      </div>
    </div>
  );
}
